import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { Paperclip, Upload, X, Image as ImageIcon, Loader2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { formatLocalDate } from '../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 20 * 1024 * 1024;

/**
 * AttachmentUploader — picks files, converts HEIC → JPEG, compresses, uploads.
 * AttachmentGallery — lists existing attachments with delete + lightbox view.
 *
 * Usage:
 *   <AttachmentUploader parentType="appointment" parentId={appt.id} onUploaded={refetch} />
 *   <AttachmentGallery parentType="appointment" parentId={appt.id} />
 *
 * Or combined via <AttachmentSection parentType="..." parentId="..." token={...} />
 */

async function maybeConvertHeic(file) {
  const isHeic =
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    /\.heic$|\.heif$/i.test(file.name || '');
  if (!isHeic) return file;
  try {
    const heic2any = (await import('heic2any')).default;
    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
    const out = Array.isArray(blob) ? blob[0] : blob;
    return new File([out], (file.name || 'photo').replace(/\.(heic|heif)$/i, '.jpg'), {
      type: 'image/jpeg',
    });
  } catch (e) {
    console.error('HEIC conversion failed', e);
    toast.error("Couldn't convert HEIC photo — please use JPG or PNG.");
    throw e;
  }
}

async function compressImage(file) {
  // Skip compression for tiny files
  if (file.size < 500 * 1024) return file;
  try {
    return await imageCompression(file, {
      maxSizeMB: 2,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      fileType: 'image/jpeg',
    });
  } catch (e) {
    console.warn('Compression failed, uploading original', e);
    return file;
  }
}

export const AttachmentUploader = ({ parentType, parentId, token, currentCount = 0, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const remaining = Math.max(0, MAX_ATTACHMENTS - currentCount);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (remaining === 0) {
      toast.error(`Maximum ${MAX_ATTACHMENTS} files per record.`);
      return;
    }
    const files = Array.from(fileList).slice(0, remaining);
    setUploading(true);
    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      const raw = files[i];
      try {
        if (raw.size > MAX_FILE_BYTES) {
          toast.error(`"${raw.name}" is too large (max 20 MB).`);
          continue;
        }
        const converted = await maybeConvertHeic(raw);
        const compressed = await compressImage(converted);
        const form = new FormData();
        form.append('file', compressed, compressed.name || 'upload.jpg');
        form.append('parent_type', parentType);
        form.append('parent_id', parentId);
        await axios.post(`${API}/api/attachments`, form, {
          headers: { Authorization: `Bearer ${token}` },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        successCount++;
      } catch (err) {
        console.error('Upload failed', err);
        toast.error(err.response?.data?.detail || `Failed to upload ${raw.name}`);
      }
    }
    setUploading(false);
    setProgress(0);
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}`);
      onUploaded && onUploaded();
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
        dragging ? 'border-primary bg-primary/5' : 'border-stone-300 bg-stone-50'
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      data-testid={`attachment-uploader-${parentType}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="text-center">
        {uploading ? (
          <div className="py-2">
            <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto mb-2" />
            <p className="text-sm text-stone-600">Uploading… {progress}%</p>
          </div>
        ) : (
          <>
            <Upload className="w-6 h-6 text-stone-400 mx-auto mb-2" />
            <p className="text-sm text-stone-700 font-medium">
              Drop photos here or
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-primary hover:underline ml-1"
                disabled={remaining === 0}
                data-testid="attachment-choose-files"
              >
                browse
              </button>
            </p>
            <p className="text-xs text-stone-500 mt-1">
              JPG, PNG, HEIC · up to 20 MB · {remaining} of {MAX_ATTACHMENTS} slots remaining
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export const AttachmentGallery = ({ parentType, parentId, token, refreshKey = 0, onChange, onCount }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // index or null
  const [blobs, setBlobs] = useState({}); // id -> blob URL

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/attachments?parent_type=${parentType}&parent_id=${encodeURIComponent(parentId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = res.data.attachments || [];
      setItems(list);
      onCount && onCount(list.length);
    } catch (e) {
      console.error('Failed to load attachments', e);
    } finally {
      setLoading(false);
    }
  }, [parentType, parentId, token, onCount]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshKey]);

  // Load blob URLs for thumbnails
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next = { ...blobs };
      for (const att of items) {
        if (next[att.id]) continue;
        try {
          const res = await axios.get(`${API}/api/attachments/${att.id}/download`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          });
          if (cancelled) return;
          next[att.id] = URL.createObjectURL(res.data);
        } catch (e) {
          console.error('Failed to load image', att.id, e);
        }
      }
      if (!cancelled) setBlobs(next);
    };
    if (items.length > 0) load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(blobs).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await axios.delete(`${API}/api/attachments/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Attachment deleted');
      fetchList();
      onChange && onChange();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="text-xs text-stone-500 py-2">Loading attachments…</div>;
  }
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2" data-testid={`attachment-gallery-${parentType}`}>
      <div className="text-xs text-stone-600 flex items-center gap-1">
        <Paperclip className="w-3 h-3" /> {items.length} attachment{items.length > 1 ? 's' : ''}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {items.map((att, idx) => (
          <div
            key={att.id}
            className="relative group aspect-square rounded-lg overflow-hidden bg-stone-100 border border-stone-200"
            data-testid={`attachment-${att.id}`}
          >
            {blobs[att.id] ? (
              <img
                src={blobs[att.id]}
                alt={att.original_filename}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setLightbox(idx)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-stone-400" />
              </div>
            )}
            <button
              type="button"
              onClick={() => handleDelete(att.id)}
              className="absolute top-1 right-1 p-1 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-opacity"
              data-testid={`delete-attachment-${att.id}`}
            >
              <Trash2 className="w-3.5 h-3.5 text-red-600" />
            </button>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox !== null && items[lightbox] && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
          >
            <X className="w-6 h-6" />
          </button>
          {lightbox > 0 && (
            <button
              className="absolute left-4 p-2 text-white hover:bg-white/10 rounded-full"
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }}
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          {lightbox < items.length - 1 && (
            <button
              className="absolute right-4 p-2 text-white hover:bg-white/10 rounded-full"
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }}
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
          <img
            src={blobs[items[lightbox].id]}
            alt={items[lightbox].original_filename}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded">
            {items[lightbox].original_filename} · {formatLocalDate(items[lightbox].created_at)}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * AttachmentSection — combines uploader + gallery. Drop into any record's form/detail view.
 */
export const AttachmentSection = ({ parentType, parentId, token, title = 'Attachments' }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [count, setCount] = useState(0);

  const handleChange = () => setRefreshKey((k) => k + 1);
  const handleCount = useCallback((n) => setCount(n), []);

  if (!parentId) return null;

  return (
    <div className="space-y-3" data-testid={`attachment-section-${parentType}`}>
      <h4 className="text-sm font-semibold text-[#2D2D2D] flex items-center gap-2">
        <Paperclip className="w-4 h-4" /> {title}
      </h4>
      <AttachmentGallery
        parentType={parentType}
        parentId={parentId}
        token={token}
        refreshKey={refreshKey}
        onChange={handleChange}
        onCount={handleCount}
      />
      <AttachmentUploader
        parentType={parentType}
        parentId={parentId}
        token={token}
        currentCount={count}
        onUploaded={handleChange}
      />
    </div>
  );
};

export default AttachmentSection;
